import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import i18n, { loadLanguage } from 'src/i18n';
import { fetchCapabilities } from 'src/models/capability/capability';
import { setupInstanaUserContext } from 'src/utils/instana';

import { FeatureFlag } from '../models/platform/_types';
import { fetchSession } from '../models/session/session';
import { fetchCurrentTrainer } from '../models/trainer/trainer';
import { fetchCurrentUser, fetchUser, updateUser } from '../models/user/user';
import { hasFeatureFlag } from '../shared/usePermissionCheck';

import { useNativeBridge } from './NativeBridgeContext';

// Import types
import type { Session } from '../models/session/_types';
import type { Trainer } from '../models/trainer/_types';
import type { SessionUser, User } from '../models/user/_types';
import type { Capability } from 'src/models/capability/_types';

interface SessionContextProps {
  session: Session | undefined;
  sessionLoading: boolean;
  sessionFetching: boolean;
  sessionError: any;
  user: SessionUser | null | undefined;
  userLoading: boolean;
  userFetching: boolean;
  userError: any;
  trainer: Trainer | undefined;
  trainerLoading: boolean;
  trainerError: any;
  capabilities: Capability[] | undefined;
  capabilitiesLoading: boolean;
  capabilitiesError: any;
  sessionTrainer: User | undefined;
  sessionTrainerLoading: boolean;
  sessionTrainerError: any;
  setDarkmode: (darkmode: boolean) => void;
  isDarkmode: () => boolean;
  updateCurrentUser: (user: Partial<User>) => void;
  disableDarkmode: () => void;
  enableDarkmode: () => void;
  refreshSessionAndUser: () => Promise<void>;
  refreshUser: () => Promise<void>;
  hasLoadedClients: boolean;
  setHasLoadedClients: (hasLoadedClients: boolean) => void;
  darkmodeDisabled: boolean;
}

interface RequiredSessionContextProps
  extends Omit<
    SessionContextProps,
    'session' | 'user' | 'trainer' | 'sessionTrainer' | 'capabilities'
  > {
  session: Session;
  user: SessionUser;
  trainer: Trainer;
  sessionTrainer: User;
  capabilities: Capability[];
}

interface SessionContextProviderProps {
  children: ReactNode;
}

const SessionContext = createContext<SessionContextProps>({
  isDarkmode: () => false,
} as SessionContextProps);

const SessionContextProvider: React.FC<SessionContextProviderProps> = ({
  children,
}) => {
  const queryClient = useQueryClient();
  const [darkmodeDisabled, setDarkmodeDisabled] = useState(false);
  const [hasLoadedClients, setHasLoadedClients] = useState(false);

  // NativeBridge needs to force darkmode via query params
  const nativeBridge = useNativeBridge();

  const { authToken } = nativeBridge;

  const {
    data: session,
    isLoading: sessionLoading,
    isFetching: sessionFetching,
    error: sessionError,
  } = useQuery({
    queryKey: ['session', authToken],
    queryFn: () => {
      return fetchSession('', { authToken });
    },
  });
  const {
    data: user,
    isLoading: userLoading,
    isFetching: userFetching,
    error: userError,
  } = useQuery({
    queryKey: ['currentUser', authToken],
    queryFn: () => {
      return fetchCurrentUser({ authToken, withImpersonator: true });
    },
    gcTime: 0,
  });

  const {
    data: trainer,
    isLoading: trainerLoading,
    error: trainerError,
  } = useQuery({
    queryKey: ['currentTrainer', user?.id, authToken],
    queryFn: () => {
      return fetchCurrentTrainer({ authToken });
    },
    enabled: !!user?.id,
    refetchOnWindowFocus: false,
    gcTime: 0,
  });

  const {
    data: sessionTrainer,
    isLoading: sessionTrainerLoading,
    error: sessionTrainerError,
  } = useQuery({
    queryKey: ['user', session?.trainerId],
    queryFn: () => {
      if (!session?.trainerId) {
        throw new Error('Trainer ID is not available');
      }

      return fetchUser({ id: session.trainerId });
    },
    enabled: !!session?.trainerId,
  });

  const {
    data: capabilities,
    isLoading: capabilitiesLoading,
    error: capabilitiesError,
  } = useQuery({
    queryKey: ['capabilities', user?.id, authToken],
    queryFn: () => {
      return fetchCapabilities({ authToken });
    },
    enabled: !!user?.id,
    gcTime: 0,
  });

  const userMutation = useMutation({
    mutationFn: (partialUser: Partial<User>) => {
      if (!user?.id) {
        throw new Error('Attempted user update without ID');
      }
      return updateUser(user.id, partialUser);
    },
    onMutate: (partialUser: Partial<User>) => {
      queryClient.setQueryData(['currentUser', authToken], {
        ...user,
        ...partialUser,
      });
    },
    onSuccess: (data) => {
      queryClient.setQueryData(['currentUser', authToken], data);
    },
  });

  const isDarkmode = useCallback(() => {
    return resolveIsDarkmode({
      session,
      user,
      darkmodeDisabled,
      nativeBridgeDarkMode: nativeBridge.darkMode,
      isWebview: nativeBridge.isWebview,
    });
  }, [
    session,
    user,
    darkmodeDisabled,
    nativeBridge.darkMode,
    nativeBridge.isWebview,
  ]);

  const updateCurrentUser = useCallback(
    (partialUser: Partial<User>) => {
      userMutation.mutate(partialUser);
    },
    [userMutation]
  );

  const setDarkmode = useCallback(
    (darkmode: boolean) => {
      updateCurrentUser({ useDarkMode: darkmode });
    },
    [updateCurrentUser]
  );

  const disableDarkmode = useCallback(() => {
    setDarkmodeDisabled(true);
  }, []);

  const enableDarkmode = useCallback(() => {
    setDarkmodeDisabled(false);
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function loadTranslation() {
      // Set the language based on priority: user locale > session locale > default 'en'
      const locale = user?.locale || session?.locale || 'en';
      await loadLanguage(locale);
      if (cancelled) return;

      // Load platform-specific translations if session ID exists
      if (session?.id && locale === 'en') {
        try {
          const translations = await import(
            `src/locales/en-US-platforms/translation-${session.id}.json`
          );
          if (cancelled) return;

          i18n.addResourceBundle(
            `en-US-${session.id}`,
            'translation',
            translations.default,
            true,
            true
          );
          i18n.changeLanguage(`en-US-${session.id}`);
          // eslint-disable-next-line no-empty
        } catch (error) {}
      }
    }

    loadTranslation();

    return () => {
      cancelled = true;
    };
  }, [session?.id, user?.locale, session?.subdomain, session?.locale]);

  useEffect(() => {
    setupInstanaUserContext(user);
  }, [user]);

  const refreshUser = useCallback(async () => {
    try {
      await queryClient.refetchQueries({
        queryKey: ['currentUser'],
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to refresh user:', error);
    }
  }, [queryClient]);

  const refreshSessionAndUser = useCallback(async () => {
    try {
      await queryClient.refetchQueries({
        queryKey: ['session'],
      });
      await queryClient.refetchQueries({
        queryKey: ['currentUser'],
      });
      await queryClient.refetchQueries({
        queryKey: ['user', session?.trainerId],
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to refresh session and user:', error);
    }
  }, [queryClient, session?.trainerId]);

  const contextValue = useMemo<SessionContextProps>(() => {
    return {
      session,
      sessionError,
      sessionLoading,
      sessionFetching,
      user,
      userLoading,
      userFetching,
      userError,
      trainer,
      trainerLoading,
      trainerError,
      sessionTrainer,
      sessionTrainerLoading,
      sessionTrainerError,
      capabilities,
      capabilitiesLoading,
      capabilitiesError,
      updateCurrentUser,
      setDarkmode,
      isDarkmode,
      darkmodeDisabled,
      disableDarkmode,
      enableDarkmode,
      refreshUser,
      refreshSessionAndUser,
      hasLoadedClients,
      setHasLoadedClients,
    };
  }, [
    session,
    sessionError,
    sessionLoading,
    sessionFetching,
    user,
    userLoading,
    userFetching,
    userError,
    trainer,
    trainerLoading,
    trainerError,
    sessionTrainer,
    sessionTrainerLoading,
    sessionTrainerError,
    capabilities,
    capabilitiesLoading,
    capabilitiesError,
    updateCurrentUser,
    setDarkmode,
    isDarkmode,
    darkmodeDisabled,
    disableDarkmode,
    enableDarkmode,
    refreshUser,
    refreshSessionAndUser,
    hasLoadedClients,
    setHasLoadedClients,
  ]);

  return (
    <SessionContext.Provider value={contextValue}>
      {children}
    </SessionContext.Provider>
  );
};

function useSession(): RequiredSessionContextProps {
  const context = useContext(SessionContext);
  const { user, session } = context;
  if (!user?.id || !session?.id) {
    throw new Error(
      'User/Session accessed through useSession without being present'
    );
  }
  return context as RequiredSessionContextProps;
}

function useOptionalSession(): SessionContextProps {
  return useContext(SessionContext);
}

/** True while initial session/user load or refetch may still establish auth. */
export function isSessionAuthPending({
  user,
  userLoading,
  userFetching,
  session,
  sessionLoading,
  sessionFetching = false,
}: {
  user: SessionUser | null | undefined;
  userLoading: boolean;
  userFetching: boolean;
  session?: Session | null;
  sessionLoading: boolean;
  sessionFetching?: boolean;
}): boolean {
  return (
    userLoading ||
    sessionLoading ||
    (userFetching && !user?.id) ||
    (sessionFetching && !session?.id)
  );
}

export function resolveIsDarkmode({
  session,
  user,
  darkmodeDisabled,
  nativeBridgeDarkMode,
  isWebview,
}: {
  session?: Session;
  user?: SessionUser | null;
  darkmodeDisabled: boolean;
  nativeBridgeDarkMode: boolean | null;
  isWebview: boolean | null;
}): boolean {
  if (!session || !hasFeatureFlag(session, FeatureFlag.DarkMode)) {
    return false;
  }

  if (nativeBridgeDarkMode !== null) {
    return nativeBridgeDarkMode;
  }

  if (darkmodeDisabled) {
    return false;
  }

  if (isWebview) {
    if (session.theme?.forcedTheme === 'dark') {
      return true;
    }
    if (session.theme?.forcedTheme === 'light') {
      return false;
    }
  }

  if (user?.useDarkMode !== undefined) {
    return user.useDarkMode;
  }

  return (
    window.matchMedia &&
    window.matchMedia('(prefers-color-scheme: dark)').matches
  );
}

export {
  useOptionalSession,
  useSession,
  SessionContextProvider,
  SessionContext,
  type User,
  type Session,
};
